import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {ModelConfigService} from '@app/model-composer';
import {CustomizationGroup} from '@app/model-composer/models/customization-group.model';
import {
  MacroColorGroup,
  MaterialColorItem,
  MaterialGroup,
  NormalizedCustomization
} from '@app/model-composer/models/customization.model';

@Injectable({ providedIn: 'root' })
export class CompositionCustomizationService {
  constructor() {
    effect(() => {
      const cfg = this.configService.config();
      if (!cfg?.customization) { return; }
      const groups = this.mapGroups(cfg.customization);
      this._groups.set(groups);
      if (!this._activeGroupId()) {
        this._activeGroupId.set(groups[0]?.id ?? null);
      }
    });
  }

  private configService = inject(ModelConfigService);
  private _groups = signal<CustomizationGroup[]>([]);
  readonly groups = this._groups.asReadonly();
  private _activeGroupId = signal<string | null>(null);
  readonly activeGroupId = this._activeGroupId.asReadonly();
  private _selection = signal<Record<string, any>>({});
  readonly selection = this._selection.asReadonly();

  readonly activeGroup = computed(() => {
    const id = this._activeGroupId();
    return this._groups().find(g => g?.id === id) ?? this._groups()?.[0] ?? null;
  });

  setActiveGroup(groupId: string): void {
    if (groupId === this._activeGroupId()) { return; }
    this._activeGroupId.set(groupId);
    this._selection.set({});
  }

  private mapGroups(raw: any[]): CustomizationGroup[] {
    return raw.map(group => ({
      id: group.id,
      label: group.label,
      customizationRows: group.customizationRows?.map((row: any) => ({
        id: row.id,
        vid: crypto.randomUUID(),
        label: row.name,
        materialOptions: row.materialOptions,
        show: row?.show
      }))
    }));
  }

  normalize(option: any): NormalizedCustomization {
    if (!option) {
      return { items: [], macroColors: [], materials: [] };
    }
    const macroMap = new Map<string, MaterialColorItem[]>();
    const materialMap = new Map<number, MaterialColorItem[]>();
    for (const item of option.materialColorList) {
      if (option.macroColorGrouping) {
        const macro = item.color.macroColor;
        const macroId = macro?.macroColorId ?? 'other';
        if (!macroMap.has(macroId)) { macroMap.set(macroId, []); }
        macroMap.get(macroId)!.push(item);
      }
      if (option.materialGrouping) {
        const matId = item.material.materialId;
        if (!materialMap.has(matId)) { materialMap.set(matId, []); }
        materialMap.get(matId)!.push(item);
      }
    }
    return {
      items: option.materialColorList,
      macroColors: option.macroColorGrouping ? this.buildMacroGroups(macroMap) : [],
      materials: option.materialGrouping ? this.buildMaterialGroups(materialMap) : []
    };
  }

  private buildMacroGroups(map: Map<string, MaterialColorItem[]>): MacroColorGroup[] {
    return Array.from(map.entries()).map(([id, items]) => ({
      id,
      label: items[0].color.macroColor?.macroColorName ?? 'Altri',
      hex: items[0].color.macroColor?.macroColorCodeHex ?? '#ccc',
      items
    }));
  }

  private buildMaterialGroups(map: Map<number, MaterialColorItem[]>): MaterialGroup[] {
    return Array.from(map.entries()).map(([id, items]) => ({
      id,
      eto: items?.[0]?.eto,
      label: items?.[0].material.materialName,
      items
    }));
  }
}
